import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchLeadById, updateLead } from '../../store/slices/leadSlice';
import StatusBadge from '../../components/StatusBadge';
import CommentSection from '../../components/CommentSection';
import { LEAD_STATUS, COMMENT_ENTITY_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';
import Loading from '../../components/Loading';

const LeadDetailModal = ({ leadId, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadLead();
  }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLead = async () => {
    setLoading(true);
    try {
      const result = await dispatch(fetchLeadById(leadId)).unwrap();
      setLead(result);
      setFormData({
        name: result.name,
        email: result.email,
        phone: result.phone,
        company: result.company,
        source: result.source || '',
        status: result.status
      });
    } catch (error) {
      alert('Failed to load lead details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const result = await dispatch(updateLead({ id: leadId, data: formData })).unwrap();
      setLead(result);
      setEditing(false);
      onSuccess();
    } catch (error) {
      alert('Failed to update lead: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="modal-backdrop fade show" />
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <Loading />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Lead Details</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            {editing ? (
              <form onSubmit={handleUpdate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-control"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Company *</label>
                    <input
                      type="text"
                      name="company"
                      className="form-control"
                      value={formData.company}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Source</label>
                    <input
                      type="text"
                      name="source"
                      className="form-control"
                      value={formData.source}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="mb-0">
                    <label className="form-label">Status *</label>
                    <select
                      name="status"
                      className="form-select"
                      value={formData.status}
                      onChange={handleChange}
                      required
                    >
                      {Object.values(LEAD_STATUS).map((status) => (
                        <option key={status} value={status}>
                          {status.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h3 className="fs-4 mb-2">{lead.name}</h3>
                    <StatusBadge status={lead.status} />
                  </div>
                  <button className="btn btn-primary" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">COMPANY</label>
                    <p className="mb-0">{lead.company}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">EMAIL</label>
                    <p className="mb-0">{lead.email}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">PHONE</label>
                    <p className="mb-0">{lead.phone}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">SOURCE</label>
                    <p className="mb-0">{lead.source || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">ASSIGNED TO</label>
                    <div className="d-flex gap-1 flex-wrap mt-1">
                      {(Array.isArray(lead.assigned_to) ? lead.assigned_to : [lead.assigned_to]).filter(Boolean).map((emp, idx) => (
                        <span key={idx} className="badge bg-primary" style={{ fontSize: '11px' }}>
                          {emp.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED</label>
                    <p className="mb-0">{formatDateTime(lead.created_at)}</p>
                  </div>
                </div>

                <hr />

                <CommentSection
                  entityType={COMMENT_ENTITY_TYPES.LEAD}
                  entityId={leadId}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadDetailModal;
